import { skipToken } from '@reduxjs/toolkit/query'
import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { Avatar } from '../../components/avatar'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TextInput } from '../../components/inputs/textInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import {
  customerSortOptions,
  FilterCustomersModal,
} from '../../components/modal/app_modals/filterCustomersModal'
import { TableComponent } from '../../components/table'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { formatCurrency, getPageFromSkip, getSkipFromPage } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import {
  useGetAllCustomersQuery,
  useGetCustomerHealthMetricsQuery,
} from '../../redux-toolkits/user/user.slice'
import {
  CustomerAcquisitionChannel,
  CustomerHealthStatus,
  CustomerSort,
  CustomersQuery,
} from '../../redux-toolkits/customers/customers.type'

const healthCards: Array<{
  status: CustomerHealthStatus
  label: string
  description: string
}> = [
  {
    status: 'active',
    label: 'Active customers',
    description: 'Has exactly one paid purchase in the selected range.',
  },
  {
    status: 'repeat',
    label: 'Repeat customers',
    description: 'Has two or more paid purchases in the selected range.',
  },
  {
    status: 'at_risk',
    label: 'At-risk customers',
    description:
      'Has purchased before, but has no paid purchases in the selected range. The last paid purchase was within 60 days of the range end.',
  },
  {
    status: 'churned',
    label: 'Churned customers',
    description:
      'Has no paid purchases in the selected range. The last paid purchase was more than 60 days before the range end.',
  },
  {
    status: 'not_activated',
    label: 'Not activated customers',
    description: 'Has no paid purchases since signing up.',
  },
]

const healthStatusStyles: Record<CustomerHealthStatus, string> = {
  active: 'bg-green-50 text-green-700',
  repeat: 'bg-blue-50 text-blue-700',
  at_risk: 'bg-amber-50 text-amber-700',
  churned: 'bg-red-50 text-red-700',
  not_activated: 'bg-gray-100 text-gray-700',
}

export const CustomersPage: React.FC = () => {
  const [query, setQuery] = useState<CustomersQuery>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { handleModalClose, handleModalOpen, setModalOpen, modal, modalOpen } =
    useModalControl()

  const {
    data: customers,
    isFetching,
    isError,
  } = useGetAllCustomersQuery(query || skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const healthMetricsQuery = query
    ? {
        isCustomDateRange: query.isCustomDateRange,
        filter: query.filter,
        startDate: query.startDate,
        endDate: query.endDate,
        registrationChannel: query.registrationChannel,
      }
    : undefined
  const { data: healthMetrics, isFetching: isHealthMetricsFetching } =
    useGetCustomerHealthMetricsQuery(healthMetricsQuery || skipToken, {
      refetchOnMountOrArgChange: true,
    })

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      search: searchParams.get('search') || undefined,
      deleted: undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      sort: (searchParams.get('sort') as CustomerSort) || undefined,
      channel:
        (searchParams.get('channel') as CustomerAcquisitionChannel) ||
        undefined,
      registrationChannel: searchParams.get('registrationChannel') || undefined,
      healthStatus:
        (searchParams.get('healthStatus') as CustomerHealthStatus) || undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) ||
          'inception_to_date',
    })
  }, [searchParams])

  const activeFilterCount = [
    query?.sort,
    query?.channel,
    query?.registrationChannel,
    query?.healthStatus,
  ].filter(Boolean).length

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <PageHeader title={`Customers`} className="capitalize !my-0" />

          <DateFilterNav
            filter={query?.filter ?? 'custom'}
            isCustomDateRange={query?.isCustomDateRange ?? false}
            startDate={query?.startDate}
            endDate={query?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
                skip: '0',
              })
            }}
          />
        </div>

        <CountCardContainer className="!my-0">
          <CountCard
            count={customers?.data?.totalCustomers ?? 0}
            text="Total customers"
            isCurrency={false}
            loading={isFetching}
          />
          <CountCard
            count={customers?.data?.totalOrderValue ?? 0}
            text="Average customer spend"
            isCurrency={true}
            loading={isFetching}
          />
          <CountCard
            count={customers?.data?.fullyOnboarded ?? 0}
            text="Total customers fully onboarded"
            isCurrency={false}
            loading={isFetching}
          />
          <CountCard
            count={customers?.data?.partiallyOnboarded ?? 0}
            text="Total customers partially onboarded"
            isCurrency={false}
            loading={isFetching}
          />
          <CountCard
            count={customers?.data?.notOnboarded ?? 0}
            text="Total customers not yet onboarded"
            isCurrency={false}
            loading={isFetching}
          />
        </CountCardContainer>

        <section className="flex flex-col gap-4">
          <HeaderSubheader
            title="Customer health"
            text="Based on paid purchases within the selected date range."
          />

          <CountCardContainer className="!my-0">
            {healthCards.map(({ status, label, description }) => {
              const metric = healthMetrics?.data?.[status]

              return (
                <CountCard
                  key={status}
                  count={metric?.count ?? 0}
                  valueSuffix={`(${metric?.percentage ?? 0}%)`}
                  text={label}
                  isCurrency={false}
                  loading={isHealthMetricsFetching}
                  selected={query?.healthStatus === status}
                  tooltip={description}
                  tooltipId="customer-health-tooltip"
                  onClick={() => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      healthStatus:
                        query?.healthStatus === status ? '' : status,
                      skip: '0',
                    })
                  }}
                />
              )
            })}
          </CountCardContainer>

          <Tooltip
            id="customer-health-tooltip"
            className="!z-[999] !w-auto !max-w-[260px] !rounded-lg !border !border-divider-gray !bg-white !px-3 !py-2 !text-left !text-sm !font-normal !leading-5 !text-dark-gray !opacity-100 !shadow-dropdown-custom"
            place="top"
            offset={8}
            delayShow={180}
            opacity={1}
            noArrow
          />
        </section>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  search: search,
                  skip: '0',
                })
              }}
              placeholder="Search name or email"
              initialValue={searchParams.get('search') || undefined}
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <Button
              type="button"
              label={`Filters${
                activeFilterCount ? ` (${activeFilterCount})` : ''
              }`}
              primary={false}
              icon="filter"
              className="!font-normal"
              onClick={() => {
                handleModalOpen('filterCustomersModal')
              }}
            />

            <Button
              type="button"
              label="Export"
              onClick={() => {
                handleModalOpen('exportDataModal')
              }}
              primary
              className=""
            />
          </div>
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            name="Customers"
            showName={false}
            headers={[
              'Customer name',
              'Phone number',
              'Email address',
              'Acquisition Channel',
              'Registration channel',
              'Health status',
              'Total orders',
              'Average order value',
            ]}
            rows={
              customers
                ? customers.data.customers.map((item) => {
                    return {
                      id: item._id,
                      content: [
                        <div
                          className="
                          flex items-center gap-3
                          "
                          key={`${item._id}-name-id`}
                        >
                          <Avatar
                            name={`${item.firstname} ${item.lastname}`}
                            useRandomBgColor
                          />
                          <Link
                            to={`/customers/${item._id}`}
                            className="
                            hover:text-black hover:underline
                            transition-all duration-500 !capitalize
                          "
                          >{`${item.firstname} ${item.lastname}`}</Link>
                        </div>,
                        item.phoneNumber,
                        item.email,
                        <span
                          className="capitalize"
                          key={`${item._id}-acquisition-channel`}
                        >
                          {item.acquisitionChannel ?? 'N/A'}
                        </span>,
                        <span key={`${item._id}-registration-channel`}>
                          {item.registeredFulfilmentCentre?.name ?? 'N/A'}
                        </span>,
                        item.healthStatus ? (
                          <span
                            key={`${item._id}-health-status`}
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium capitalize ${
                              healthStatusStyles[item.healthStatus]
                            }`}
                          >
                            {item.healthStatus.replace('_', ' ')}
                          </span>
                        ) : (
                          'N/A'
                        ),
                        `${item.totalOrders}`,
                        formatCurrency(item.averageOrderValue ?? 0),
                      ],
                    }
                  })
                : []
            }
            onPageChange={(page) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                skip: `${getSkipFromPage(page, Number(query?.limit))}`,
              })
            }}
            pageLimit={Number(query?.limit ?? 20)}
            currentPage={getPageFromSkip(
              Number(query?.skip),
              Number(query?.limit),
            )}
            loading={isFetching}
            emptyText={
              query?.healthStatus
                ? `No ${query.healthStatus.replace(
                    '_',
                    '-',
                  )} customers found for this date range`
                : 'No customers found'
            }
            isEmpty={
              [0, undefined].includes(customers?.data?.customers?.length) ||
              isError
            }
            totalDataCount={customers?.data.count ?? 0}
          />
        </div>
      </div>

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          customersSort: query?.sort,
          channel: query?.channel,
          registrationChannel: query?.registrationChannel,
          healthStatus: query?.healthStatus,
          search: query?.search,
          isCustomDateRange: query?.isCustomDateRange,
          filter: query?.filter,
          startDate: query?.startDate,
          endDate: query?.endDate,
        }}
        title="Export Customers"
        reportType="customers"
      >
        {(form) => {
          return (
            <Dropdown
              menuClassName=""
              menuButton={
                <TextInput
                  type="text"
                  name="customersSort"
                  placeholder=""
                  value={`${
                    form.values?.customersSort
                      ? customerSortOptions.find(
                          (filter) =>
                            filter.value === form.values?.customersSort,
                        )?.label
                      : 'Default'
                  }`}
                  onBlur={form.handleBlur}
                  onChange={form.handleChange}
                  errors={''}
                  hasIcon={false}
                  extra={
                    <Icon
                      id={'left-caret'}
                      width={16}
                      height={16}
                      className="-rotate-90 text-sec-black"
                    />
                  }
                />
              }
              menuItems={customerSortOptions.map((filter) => ({
                name: filter.label,
                value: filter.value ?? 'default',
                onClick: () => {
                  form.setFieldValue('customersSort', filter.value)
                },
              }))}
            />
          )
        }}
      </ExportDataModal>

      <FilterCustomersModal
        modalOpen={modalOpen && modal === 'filterCustomersModal'}
        handleModalClose={handleModalClose}
        setModalOpen={setModalOpen}
        query={query}
        onSuccess={(updatedQuery) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            sort: updatedQuery.sort || '',
            channel: updatedQuery.channel || '',
            registrationChannel: updatedQuery.registrationChannel || '',
            healthStatus: updatedQuery.healthStatus || '',
            skip: '0',
          })
        }}
      />
    </Layout>
  )
}
